import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ─── Shared Zod schemas ───────────────────────────────────────────────────────

export const ZCmsFieldType = z.enum([
  'boolean',
  'date',
  'int',
  'decimal',
  'string',
  'enum',
  'html',
  'image',
  'json',
  'url',
]);

export const ZCmsFieldDef = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: ZCmsFieldType,
  required: z.boolean().optional(),
  enumOptions: z.string().array().optional(),
});

export const ZCmsDocumentStatus = z.enum(['DRAFT', 'PUBLISHED']);

export const ZCmsDocumentResponse = z.object({
  id: z.number(),
  typeSlug: z.string(),
  data: z.record(z.unknown()),
  status: ZCmsDocumentStatus,
  sortOrder: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const ZNotFound = z.object({ message: z.string() });
const ZBadRequest = z.object({ message: z.string() });

// ─── Contract ─────────────────────────────────────────────────────────────────

export const adminCmsContract = c.router({
  // ── Documents ──────────────────────────────────────────────────────────────

  listDocuments: {
    method: 'GET',
    path: '/types/:typeSlug/documents',
    pathParams: z.object({ typeSlug: z.string() }),
    query: z.object({
      status: ZCmsDocumentStatus.optional(),
    }),
    responses: {
      200: ZCmsDocumentResponse.array(),
    },
    summary: 'List documents for a CMS type',
  },

  createDocument: {
    method: 'POST',
    path: '/types/:typeSlug/documents',
    pathParams: z.object({ typeSlug: z.string() }),
    body: z.object({
      data: z.record(z.unknown()),
      status: ZCmsDocumentStatus.optional(),
      sortOrder: z.number().int().optional(),
    }),
    responses: {
      201: ZCmsDocumentResponse,
      400: ZBadRequest,
    },
    summary: 'Create a document for a CMS type',
  },

  getDocument: {
    method: 'GET',
    path: '/types/:typeSlug/documents/:id',
    pathParams: z.object({ typeSlug: z.string(), id: z.coerce.number() }),
    responses: {
      200: ZCmsDocumentResponse,
      404: ZNotFound,
    },
    summary: 'Get a CMS document by ID',
  },

  updateDocument: {
    method: 'PUT',
    path: '/types/:typeSlug/documents/:id',
    pathParams: z.object({ typeSlug: z.string(), id: z.coerce.number() }),
    body: z.object({
      data: z.record(z.unknown()).optional(),
      status: ZCmsDocumentStatus.optional(),
      sortOrder: z.number().int().optional(),
    }),
    responses: {
      200: ZCmsDocumentResponse,
      400: ZBadRequest,
      404: ZNotFound,
    },
    summary: 'Update a CMS document',
  },

  deleteDocument: {
    method: 'DELETE',
    path: '/types/:typeSlug/documents/:id',
    pathParams: z.object({ typeSlug: z.string(), id: z.coerce.number() }),
    body: c.noBody(),
    responses: {
      200: z.object({ success: z.boolean() }),
      404: ZNotFound,
    },
    summary: 'Delete a CMS document',
  },

  // ── Image upload ───────────────────────────────────────────────────────────

  uploadImage: {
    method: 'POST',
    path: '/upload-image',
    contentType: 'multipart/form-data',
    body: c.type<{ image: File }>(),
    responses: {
      200: z.string(),
      400: ZBadRequest,
    },
    summary: 'Upload a CMS image to S3',
  },
}, {
  pathPrefix: '/admin',
  strictStatusCodes: true,
  commonResponses: {
    401: c.type<{ message: string; statusCode: 401 }>(),
    403: c.type<{ message: string; statusCode: 403 }>(),
  },
});
